import { BackendConfig } from '../config/backend';
import { getCookie, setCookie } from '@/utils/cookies';
import { useAuthStore } from '@/store/authStore';

export const BASE_URL = BackendConfig.websiteApi;

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const attemptTokenRefresh = async (): Promise<string> => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const refreshToken = getCookie('refresh_token');
    if (!refreshToken) throw new Error('No refresh token available');

    const response = await fetch(`${BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Refresh session failed');

    const result = await response.json();
    const newToken = result.accessToken;

    localStorage.setItem('token', newToken);
    if (result.refreshToken) {
      setCookie('refresh_token', String(result.refreshToken), {
        maxAge: 7 * 24 * 60 * 60,
        path: '/',
      });
    }

    processQueue(null, newToken);
    return newToken;
  } catch (error) {
    processQueue(error, null);
    localStorage.removeItem('token');
    setCookie('refresh_token', '', {
      maxAge: 0,
      path: '/',
    });
    useAuthStore.getState().logout();
    throw error;
  } finally {
    isRefreshing = false;
  }
};

const handleResponse = async (response: Response) => {
  const contentType = response.headers.get('content-type');
  const data = contentType?.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const error: any = new Error(data?.detail || data?.message || 'API Error');
    error.response = { status: response.status, data };
    throw error;
  }
  return data;
};

const request = async (endpoint: string, options: RequestInit, includeAuth: boolean) => {
  const headers = new Headers(options.headers);

  if (includeAuth) {
    const token = localStorage.getItem('token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }


  const response = await fetch(`${BASE_URL}${endpoint}`, { ...options, headers });

  if (response.status === 401 && includeAuth) {
    const newToken = await attemptTokenRefresh();

    headers.set('Authorization', `Bearer ${newToken}`);
    const retryResponse = await fetch(`${BASE_URL}${endpoint}`, { ...options, headers });

    return await handleResponse(retryResponse);
  }

  return await handleResponse(response);

};

export const apiGet = (endpoint: string, includeAuth = false) =>
  request(endpoint, { method: 'GET' }, includeAuth);

export const apiPost = (endpoint: string, data: unknown, includeAuth = false) =>
  request(
    endpoint,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
    includeAuth
  );

export const apiPut = (endpoint: string, data: unknown, includeAuth = false) =>
  request(
    endpoint,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
    includeAuth
  );

export const apiPatch = (endpoint: string, data: unknown, includeAuth = false) =>
  request(
    endpoint,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
    includeAuth
  );

export const apiDelete = (endpoint: string, includeAuth = false) =>
  request(endpoint, { method: 'DELETE' }, includeAuth);


/** The bearer header for a hand-rolled fetch (file downloads); empty when signed out. */
export const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
